import React, {useEffect, useMemo, useState} from 'react';
import {Button, Col, Row} from "react-bootstrap";
import {parse, format, isSameDay, isToday, getDay} from "date-fns"
import {fr} from "date-fns/locale"
import SlideAnimation from "./SlideAnimation";
import {Box, FormControl, InputLabel, MenuItem, Select, useMediaQuery} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronLeft} from "@fortawesome/free-solid-svg-icons";
import cx from "classnames"
import sandbox from "../../../service/Sandbox";
import AgencyHours from "./AgencyHours";
import {
    filterAvailabilitiesForClosing,
    getClosingPeriodDisplayItems,
    isHourBlockedByClosing,
    isTimeInClosingPeriod,
} from "../../utils/closingPeriods";

// Les deux colonnes du picker s'empilent sous le breakpoint `lg` de Bootstrap (992px).
// C'est à partir de là qu'on bascule sur un rendu tactile (mobile / tablette).
const MOBILE_BREAKPOINT_QUERY = '(max-width: 991.98px)'
const MOBILE_MEDIA_QUERY = `@media ${MOBILE_BREAKPOINT_QUERY}`
const DESKTOP_MEDIA_QUERY = '@media (min-width: 992px)'

// Sur mobile le Select n'est plus `native` (voir plus bas) : on recentre la valeur affichée.
const timeSelectSx = {
    [MOBILE_MEDIA_QUERY]: {
        '& .MuiSelect-select': {
            textAlign: 'center',
        },
    },
}

// Liste déroulante tactile sur mobile : le menu natif du navigateur est illisible
// (lignes de quelques pixels), on passe donc par le menu MUI que l'on peut dimensionner.
// `disablePortal` : le menu reste dans le DOM du Modal react-bootstrap, sinon son `enforceFocus`
// et le focus trap MUI se renvoient le focus en boucle (menu qui saute / se ferme sur iOS).
const mobileMenuProps = {
    disablePortal: true,
    PaperProps: {
        sx: {
            maxHeight: '60vh',
            '& .MuiMenuItem-root': {
                fontSize: '1.25rem',
                minHeight: '52px',
                justifyContent: 'center',
            },
        },
    },
}

const TimePicker = ({day, mode, name, value, onBackButtonClick, onChange, withLimit = false, min, max, hourDiff = 12, maxHourDiff = 0, agencyHours = null, agency, hoursAndMinutes = null, confirmDisabled = false}) => {

    const [startDate, setStartDate] = useState(null)
    const [hoursRange, setHoursRange] = useState([])
    const [minutesRange, setMinutesRange] = useState([])

    // En dessous de `lg`, on abandonne le select natif au profit du menu MUI, seul dimensionnable.
    const isMobileLayout = useMediaQuery(MOBILE_BREAKPOINT_QUERY)

    const [state, setState] = useState({
        hour: '12',
        minute: '00'
    })
    const [disabledHours, setDisabledHours] = useState([])

    const agencyAvailabilityValues = useMemo(() => {
        // agency_name, st
        if(agency && day) {
            const dayOfTheWeek = getDay(parse(day, 'yyyy-MM-dd', new Date()))
            const periods = agency?.delivery_periods ?? agency?.availability_periods ?? []
            const result = periods?.filter(item => item.day === dayOfTheWeek) ?? []
            return result?.sort((item1, item2) => {
                if (item1.start_hour > item2.start_hour) {return 1}
                if (item1.start_hour < item2.start_hour) {return -1}
                return 0
            })
        }
    }, [agency, day]);

    const agencyOperatingHours = useMemo(() => {
        if(agency && day) {
            const dayOfTheWeek = getDay(parse(day, 'yyyy-MM-dd', new Date()))
            const type = mode === "start" ? "booking_start" : "booking_end"
            const result = agency?.operating_hours?.filter(item => item.day === dayOfTheWeek && item.type === type) ?? []
            return result?.sort((item1, item2) => {
                if (item1.min_hour > item2.min_hour) {return 1}
                if (item1.min_hour < item2.min_hour) {return -1}
                return 0
            })
        }
    }, [agency, day])

    const agencyName = useMemo(() => {
        if(agency) {
            if(agency?.public_name) {return agency.public_name} else {return  agency.name}
        }
        return null
    }, [agency])

    const bookingDateForClosing = useMemo(() => {
        if (!day) return null;
        return parse(day, 'yyyy-MM-dd', new Date());
    }, [day]);

    const closingPeriodItems = useMemo(() => {
        if (!bookingDateForClosing || !agency?.closing_periods?.length) return [];
        return getClosingPeriodDisplayItems(agency.closing_periods, bookingDateForClosing);
    }, [agency?.closing_periods, bookingDateForClosing]);

    const displayAvailabilities = useMemo(() => {
        if (!agencyAvailabilityValues?.length) return agencyAvailabilityValues;
        return filterAvailabilitiesForClosing(
            agencyAvailabilityValues,
            bookingDateForClosing,
            agency?.closing_periods
        );
    }, [agencyAvailabilityValues, bookingDateForClosing, agency?.closing_periods]);
    useEffect(() => {
        if(day) {
            const date = parse(day, 'yyyy-MM-dd', new Date())
            setStartDate(date)
        }
    }, [day]);

    useEffect(() => {
        if(value) {
            const split = value?.split(':')
            if(split?.length) {
                if(agencyHours?.length) {
                    const minHour = agencyHours[0]
                    const maxHour = agencyHours[agencyHours?.length -1]
                    let selectedHour = split[0]
                    if (Number(selectedHour) < minHour) {
                        selectedHour = String(minHour).padStart(2, '0')
                    } else if (Number(selectedHour) > maxHour) {
                        selectedHour = String(maxHour).padStart(2, '0')
                    }
                    setState({hour: selectedHour, minute: split[1]})
                } else {
                    setState({hour: split[0], minute: split[1]})
                }
            }
        }
    }, [value, agencyHours])

    useEffect(() => {
        if(day) {
            let hourList = []
            const date = parse(day, 'yyyy-MM-dd', new Date())
            if (isToday(date)) {
                const minHour = format(new Date(), 'HH')
                sandbox.range(0, parseInt(minHour)).forEach(nb => {
                    hourList = [...hourList, nb]
                })
            }
            if(withLimit) {
                if(min) {
                    const split = min?.split(':')
                    if(split?.length) {
                        sandbox.range(0, parseInt(split[0]) + hourDiff ).forEach(nb => {
                            if(!hourList?.includes(nb)) {
                                hourList = [...hourList, nb]
                            }
                        })
                        if(maxHourDiff > 0) {
                            sandbox.range(parseInt(split[0]) + maxHourDiff + 1, 25).forEach(nb => {
                                if(!hourList?.includes(nb)) {
                                    hourList = [...hourList, nb]
                                }
                            })
                        }
                    }
                }
                if(max) {
                    const split = max?.split(':')
                    if(split?.length) {
                        sandbox.range(parseInt(split[0]) - hourDiff, 25).forEach(nb => {
                            if(!hourList?.includes(nb)) {
                                hourList = [...hourList, nb]
                            }
                        })
                    }
                }
            }
            hourList = hourList.filter(h => agencyHours.includes(h))
            if (bookingDateForClosing && agency?.closing_periods?.length) {
                agencyHours.forEach((h) => {
                    if (isHourBlockedByClosing(bookingDateForClosing, h, agency.closing_periods) && !hourList.includes(h)) {
                        hourList = [...hourList, h];
                    }
                });
            }
            setDisabledHours(hourList)
        }
    }, [min, max, withLimit, hourDiff, maxHourDiff, day, agencyHours, agency?.closing_periods, bookingDateForClosing])

    useEffect(() => {
        if (agencyHours !== null){
            let newHoursRange = sandbox.range(0,24)
            if (agencyHours.length > 0){
                newHoursRange = newHoursRange.filter(h => agencyHours.includes(h))
            }
            setHoursRange(newHoursRange)
        }
    }, [agencyHours]);


    useEffect(() => {
        let ranges = [0]
        let matchWithAgencyHours = false
        
        if (hoursAndMinutes?.length > 0 ){
            hoursAndMinutes?.filter(h => h.type === "end")?.map(h => h.time)?.forEach((h) => {
                const hour = h.split(":")[0]
                const minute = h.split(":")[1]
                if (hour === state.hour) {
                    matchWithAgencyHours = true
                    if (minute == 15) {
                        ranges = [...ranges, 15]
                    }
                    if (minute == 30) {
                        ranges = [...ranges,15, 30]
                    }
                    if (minute == 45) {
                        ranges = [...ranges,15,30,45]
                    }
                    
                }
            })
        }

        if (hoursAndMinutes?.length > 0){
            hoursAndMinutes?.filter(h => h.type === "start")?.map(h => h.time)?.forEach((h) => {
                const hour = h.split(":")[0]
                const minute = h.split(":")[1]
                if (hour === state.hour) {
                    matchWithAgencyHours = true
                    ranges = []
                    if (minute == 0) {
                        ranges = [0, 15, 30, 45]
                    }
                    if (minute == 15) {
                        ranges = [15, 30, 45]
                    }
                    if (minute == 30) {
                        ranges = [30, 45]
                    }
                    if (minute == 45) {
                        ranges = [45]
                    }
                }
            })

        }
        if (!matchWithAgencyHours){
            ranges = [0,15,30,45]
        }
        if (bookingDateForClosing && agency?.closing_periods?.length) {
            ranges = ranges.filter((m) => !isTimeInClosingPeriod(
                bookingDateForClosing,
                state.hour,
                m,
                agency.closing_periods
            ));
        }
        // remove duplicates from the ranges array
        ranges = [...new Set(ranges)]
        setMinutesRange(ranges)

        if (ranges.length === 0) return;

        const minuteNum = parseInt(state.minute, 10);
        if (!ranges.includes(minuteNum)) {
            const newMinute = sandbox.numberPad(ranges[0], 2);
            const newValue = `${state.hour}:${newMinute}`;
            if (value !== newValue) {
                setState((prev) => ({ ...prev, minute: newMinute }));
                onChange?.({ name, value: newValue }, false);
            }
        }
    }, [state.hour, bookingDateForClosing, agency?.closing_periods, hoursAndMinutes, value])

    const handleBackButtonClick = () => {
        onBackButtonClick && onBackButtonClick()
    }

    const handleChange = ({name_input, value}) => {
        if(name_input === 'hour') {
            onChange && onChange({name, value: `${value}:${state.minute}`}, false)
        } else if (name_input === 'minutes') {
            onChange && onChange({name, value: `${state.hour}:${value}`})
        }
    }

    const handleMinuteItemClick = (value) => {
        if(value === state.minute) {
            onChange && onChange({name, value: `${state.hour}:${value}`})
        }
    }
    const handleHourItemClick = (value) => {
        if(value === state.hour) {
            onChange && onChange({name, value: `${value}:${state.minute}`}, false)
        }
    }

    const handleConfirmButtonCLick = () => {
        if (confirmDisabled) {
            return
        }

        let selectedHour = state.hour
        const formattedHours = hoursRange?.map((nb) => sandbox.numberPad(nb, 2))
        if(!formattedHours.includes(state.hour)) {
            selectedHour = formattedHours[0]
        }
        onChange && onChange({name, value: `${selectedHour}:${state.minute}`})
    }

    return (
        <SlideAnimation className={"w-100 d-flex align-items-center"}>
            <Row className={"w-100 h-100 align-items-center"}>
                <Col lg={6} className={cx("d-flex", {"justify-content-around": startDate == null}, {'justify-content-start': startDate})}>
                    <div className={"d-flex flex-column align-items-center w-100"}>
                        <div className={cx("w-100 d-flex" ,{"justify-content-around" : day === ""})}>
                            <p className={"rounded-circle c-main pointer bold  text-start p-3 border d-flex w20 h20 justify-content-center align-items-center"} onClick={handleBackButtonClick}>
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </p>
                            {!day && (<p className={"medium fs18 text-center"}>Veuillez indiquer le jour de {mode === "start" ? "début" : "retour"}</p>)}
                        </div>
                        {day && (
                            <>
                                <p className={"medium fs18"}>Date de {mode === "start" ? "début" : "retour"}</p>
                                <p className={"mt-3"}> {startDate != null && (format(startDate, 'EEEE dd MMMM yyyy', {locale: fr} ))}</p>
                                {agencyAvailabilityValues && (
                                    <div className={"mt-3"}>
                                        <p className={"medium text-center"}>Agence {agencyName}</p>
                                        {(displayAvailabilities?.length > 0 || closingPeriodItems?.length > 0) && (
                                            <AgencyHours
                                                availabilities={displayAvailabilities}
                                                closingPeriodItems={closingPeriodItems}
                                            />
                                        )}
                                    </div>
                                )}

                                {agencyOperatingHours && (
                                    <div className={"mt-3"}>
                                        <p className={"medium text-center"}></p>
                                        {agencyOperatingHours?.length > 0 && (
                                            <AgencyHours
                                                agencyOperatingHours={agencyOperatingHours}
                                                mode={mode}
                                                availabilities={displayAvailabilities}
                                                closingPeriodItems={closingPeriodItems}
                                            />
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Col>
                <Box
                    component={Col}
                    lg={6}
                    className={"h-100 d-flex align-items-center"}
                    sx={{
                        // Le liseré vertical n'a de sens que lorsque les colonnes sont côte à côte.
                        [DESKTOP_MEDIA_QUERY]: {
                            borderLeft: '1px solid #dee2e6',
                        },
                        // En pile, on aère la zone de saisie et on sépare avec un liseré horizontal.
                        [MOBILE_MEDIA_QUERY]: {
                            marginTop: '1.5rem',
                            paddingTop: '1.5rem',
                            borderTop: '1px solid #dee2e6',
                        },
                    }}
                >
                    {day && (
                        <div className={"d-flex flex-column w-100"}>
                            <p className={"d-lg-none text-center medium fs16 mb-3"}>
                                Heure de {mode === "start" ? "début" : "retour"}
                            </p>
                            <div className={"d-flex justify-content-around w-100 align-items-center"}>
                                <FormControl
                                    className={"w30p bold text-center"}
                                    sx={timeSelectSx}
                                >
                                        <Select
                                            value={state.hour}
                                            name={'hour'}
                                            native={!isMobileLayout}
                                            MenuProps={isMobileLayout ? mobileMenuProps : undefined}
                                            inputProps={{className: "text-center medium"}}
                                            onChange={(e) => handleChange({name_input: e.target.name,value: e.target.value})}
                                        >
                                            {hoursRange?.map((nb) => {
                                                const itemValue = sandbox.numberPad(nb, 2)
                                                const isDisabled = disabledHours.includes(nb)
                                                return isMobileLayout ? (
                                                    <MenuItem
                                                        key={`hour-option-${mode}-${nb}`}
                                                        value={itemValue}
                                                        disabled={isDisabled}
                                                    >
                                                        {itemValue}
                                                    </MenuItem>
                                                ) : (
                                                    <option
                                                        key={`hour-option-${mode}-${nb}`}
                                                        value={itemValue}
                                                        disabled={isDisabled}
                                                    >
                                                        {itemValue}
                                                    </option>
                                                )
                                            })}
                                        </Select>
                                </FormControl>
                                <p className={"bold fs24"}>:</p>
                                <FormControl
                                    className={"w30p bold text-center"}
                                    sx={timeSelectSx}
                                >
                                    <Select
                                        value={state.minute}
                                        name={'minutes'}
                                        native={!isMobileLayout}
                                        MenuProps={isMobileLayout ? mobileMenuProps : undefined}
                                        inputProps={{className: "text-center medium"}}
                                        onChange={(e) => handleChange({name_input: e.target.name,value: e.target.value})}
                                    >
                                        {minutesRange?.map((nb) => {
                                            const itemValue = sandbox.numberPad(nb, 2)
                                            return isMobileLayout ? (
                                                <MenuItem
                                                    key={`minutes-option-${mode}-${nb}`}
                                                    value={itemValue}
                                                >
                                                    {itemValue}
                                                </MenuItem>
                                            ) : (
                                                <option
                                                    key={`minutes-option-${mode}-${nb}`}
                                                    value={itemValue}
                                                >
                                                    {itemValue}
                                                </option>
                                            )
                                        })}
                                    </Select>
                                </FormControl>
                            </div>
                            <Box
                                className={"mt-5 d-flex justify-content-end"}
                                sx={{
                                    // Bouton pleine largeur sur mobile pour une cible tactile confortable.
                                    [MOBILE_MEDIA_QUERY]: {
                                        marginTop: '2rem !important',
                                        '& .btn': {
                                            width: '100%',
                                            paddingTop: '14px',
                                            paddingBottom: '14px',
                                            fontSize: '1.1rem',
                                        },
                                    },
                                }}
                            >
                                <Button
                                    className={cx({ "opacity-50": confirmDisabled })}
                                    onClick={handleConfirmButtonCLick}
                                    disabled={confirmDisabled}
                                    variant={confirmDisabled ? "secondary" : "primary"}
                                    aria-disabled={confirmDisabled}
                                >
                                    Valider
                                </Button>
                            </Box>
                        </div>
                    )}
                </Box>
            </Row>
        </SlideAnimation>

    );
};

export default TimePicker;
